import classNames from 'classnames';
import { Popup, Template } from 'devextreme-react';
import { dxElement } from 'devextreme/core/element';
import DataSource, { DataSourceOptions } from 'devextreme/data/data_source';
import dxForm from 'devextreme/ui/form';
import { LogisticUnitSimpleTextDataKeys } from 'fulogics-ui-web/dist/layout/components/logistic-unit-simple-text/LogisticUnitSimpleText';
import RouteLocationModelSimpleText from 'fulogics-ui-web/dist/layout/components/route-location-model-simple-text/RouteLocationModelSimpleText';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import DataGrid from 'fulogics-ui-web/dist/layout/dx/DataGrid/DataGrid';
import Form from 'fulogics-ui-web/dist/layout/dx/Form/Form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { nameof } from 'ts-simple-nameof';
import {
    COSOrderForwardStepStore,
    setLocationPayload,
    setPlannedStartDatePayload,
} from '../../../../store/models/COSOrderForwardStepModel';
import { ForwardStepDestinationLocationsStore } from '../../../../store/models/ForwardStepDestinationLocationsModel';
import { iRootDispatch, iRootState } from '../../../../store/store';
import PayloadAddForm, {
    PayloadAddFormPayloadData,
    PayloadAddFormPayloadDataKeys,
} from '../../payload-add-form/PayloadAddForm';
import { OrderForwardStepData, OrderForwardStepDataKeys } from '../OrderForwardStepData';
import {
    OrderForwardStepFormFormId,
    OrderForwardStepFormGridId,
    OrderForwardStepFormHeaderId,
    orderForwardStepFormValidationGroup,
    orderStepsForwardAddPayloadButtonId,
    orderStepsForwardRemoveStepButtonId,
    orderStepsForwardShowExternalLocationsCheckBoxId,
} from './inspectorOptions';
import styles from './OrderForwardStepForm.module.scss';

import { custom } from 'devextreme/ui/dialog';
import { isDate } from 'fulogics-ui-core/dist/devExtremeInterop/type';
import { useFormData } from 'fulogics-ui-core/dist/layout/hooks/useFormData';
import CheckBox from 'fulogics-ui-web/dist/layout/dx/CheckBox/CheckBox';
import { useMemoDataSource } from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';
import { useVStore } from 'fulogics-ui-web/dist/layout/hooks/useVStore';
import InspectorVariablesContext from 'fulogics-ui-web/dist/layout/inspector/context/InspectorVariablesContext';
import moment from 'moment';
import { deleteItemPayload } from '../../../../store/models/COSOrderForwardStepModel';
import { COSOrderForwardStepPayloadStore } from '../../../../store/models/COSOrderForwardStepPayloadModel';
import i18n from '../../../../translations/i18n';
import PayloadCarrierCountSimpleTextTemplate from '../../../templates/dataGrid/column/payload-carrier-count-simple-text-template/PayloadCarrierCountSimpleTextTemplate';
import AllowedStacksHeaderTemplate from '../../../templates/dataGrid/header/allowed-stacks-header-template/AllowedStacksHeaderTemplate';
import { RemainingTrailerSpace } from '../../progress-bar-boxes/ProgressBarBoxes';

const mapStateToProps = (state: iRootState) => ({
    createDestinationLocationsDataSource: (isInternal?: boolean) => {
        let opts = {
            store: ForwardStepDestinationLocationsStore,
            filter: ['isInternal', '=', isInternal],
        } as DataSourceOptions;
        //
        opts.select = LogisticUnitSimpleTextDataKeys;
        //
        return opts;
    },
    createPayloadDataSource: (payloadCollectionId?: string) => {
        let opts = COSOrderForwardStepPayloadStore.createByCollectionId(payloadCollectionId);
        //
        if (opts) opts.select = OrderForwardStepPayloadItemDataKeys;
        //
        return opts;
    },
});

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: OrderForwardStepFormDisplayOptions) =>
    ({
        setPlannedStartDate: _dispatch?.COSOrderForwardStep?.setPlannedStartDate,
        setLocation: _dispatch?.COSOrderForwardStep?.setLocation,
        removeStep: _dispatch?.COSOrderForwardStep?.deleteItem,
        //
        ..._ownDispatch,
    } as OrderForwardStepFormDisplayOptions);

const OrderForwardStepForm = connect(
    mapStateToProps,
    mapDispatch as any
)((props: OrderForwardStepFormProps) => {
    const { t } = useTranslation('OrderForwardStepForm');
    const [showExternalLocations, setShowExternalLocations] = useState<boolean>(false);

    const payloadDataSource = useMemoDataSource(
        () => props.createPayloadDataSource?.(props.data?.payload?.id),
        [props.data?.payload?.id]
    );
    const destinationLocationsDataSource = useMemoDataSource(
        () => props.createDestinationLocationsDataSource?.(!showExternalLocations),
        [showExternalLocations]
    );

    const data = useMemo(
        () =>
            ({
                ...props.data,
                plannedStartDate: props.data?.plannedStartDate,
            } as OrderForwardStepData),
        [props.data]
    );

    const { formData, updateFieldFormData } = useFormData(data);
    const vStore = useVStore(
        () => ({
            dataSources: {
                destinationLocationsDataSource,
            },
            variables: {
                minPlannedStartDate: moment().add(3, 'hours').toDate(),
            },
        }),
        [destinationLocationsDataSource]
    );

    const [addPayloadPopupVisible, setAddPayloadPopupVisible] = useState(false);

    const onAddPayloadButtonClick = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setAddPayloadPopupVisible(true);
    }, []);

    const onFieldDataChanged = async (e: {
        component?: dxForm;
        element?: dxElement;
        model?: any;
        dataField?: string;
        value?: any;
    }) => {
        // @ts-ignore
        if (e.component?._requireRefresh) return;
        //
        props.onFieldDataChanged?.(e.dataField, e.value);
        //

        if (e.dataField === nameof<OrderForwardStepData>((c) => c.plannedStartDate)) {
            const value = isDate(e.value) ? e.value?.toISOString() : e.value;

            await updateFieldFormData(
                e.dataField,
                e.value,
                (value) => {
                    return {
                        plannedStartDate: value,
                    };
                },
                async (id, _) => {
                    await COSOrderForwardStepStore.update(id, {
                        plannedStartDate: value,
                    });
                }
            );
        } else if (e.dataField === nameof<OrderForwardStepData>((c) => c.location!.logisticUnit)) {
            await updateFieldFormData(
                e.dataField,
                e.value,
                (value) => {
                    return {
                        location: {
                            logisticUnitId: value?.id ?? '-1',
                            logisticUnit: value,
                        },
                    };
                },
                async (id, value) => {
                    await props.setLocation?.({
                        id: props.data?.id ?? '-1',
                        //
                        location: {
                            logisticUnitId: value?.id ?? '-1',
                            logisticUnit: value,
                        },
                    });
                },
                (fd) => {
                    return fd?.location;
                }
            );
        }
    };

    const removeStep = useCallback(
        async () => await props.removeStep?.({ id: props.data?.id ?? '-1' }),
        [props.removeStep, props.data?.id]
    );

    const onRemoveStepDialog = useCallback(() => {
        return custom({
            messageHtml: `<div class="confirmCustomDialogContainer">
        <div class="confirmCustomDialogContainerTop">${t(
            'removePlace'
        )}</div><div class="confirmCustomDialogContainerBottom">${t(
                'confirmRemovePlace'
            )}</div></div>`,
            showTitle: false,
            buttons: [
                {
                    // text: "t~OrderNewScreenDonePopup:confirmOrder",
                    text: i18n.t('OrderForwardStepForm:cancel'),
                    type: 'danger',
                    stylingMode: 'contained',
                },
                {
                    stylingMode: 'contained',
                    type: 'success',
                    // text: "t~OrderNewScreenDonePopup:cancel",
                    text: i18n.t('OrderForwardStepForm:confirm'),
                    onClick: (e) => {
                        removeStep?.();
                    },
                },
            ],
        });
    }, [t, removeStep]);

    const removeStepStart = useCallback(() => {
        onRemoveStepDialog().show();
    }, [onRemoveStepDialog]);

    const onPayloadPopupHiding = useCallback(() => {
        setAddPayloadPopupVisible(false);
        //
        props.onFieldDataChanged?.(
            nameof<OrderForwardStepFormData>((q) => q.payload),
            formData.payload
        );
    }, [props.onFieldDataChanged, formData.payload]);

    useEffect(() => {
        const handleOrderStepsForwardDataSourceChanged = () => {
            props.onStepsForwardDataChanged?.();
        };
        payloadDataSource?.on('changed', handleOrderStepsForwardDataSourceChanged);
        return () => {
            payloadDataSource?.off('changed', handleOrderStepsForwardDataSourceChanged);
        };
    }, [payloadDataSource, props.onStepsForwardDataChanged]);

    useEffect(() => {
        if (props?.data?.id && !props?.data?.plannedStartDate) {
            const value = moment().add(3, 'hours').add(10, 'minutes').toDate();
            const fieldName = nameof<OrderForwardStepData>((c) => c.plannedStartDate);

            updateFieldFormData(
                fieldName,
                value,
                (value) => {
                    return {
                        plannedStartDate: value,
                    };
                },
                async (id, _) => {
                    await COSOrderForwardStepStore.update(id, {
                        plannedStartDate: value.toISOString(),
                    });
                }
            );

            props.onFieldDataChanged?.(fieldName, value);
        }
    }, [props?.data?.id, updateFieldFormData]);

    return (
        <div className={classNames(styles.container, props.className)}>
            <InspectorVariablesContext store={vStore}>
                <form id={orderForwardStepFormValidationGroup} onSubmit={onAddPayloadButtonClick}>
                    {!props.isReadOnly && (
                        <Popup
                            visible={addPayloadPopupVisible}
                            onHiding={onPayloadPopupHiding}
                            dragEnabled={false}
                            showTitle={false}
                            width="95%"
                            height="95%"
                        >
                            <PayloadAddForm
                                data={formData}
                                createPayloadDataSource={() => payloadDataSource}
                                //
                                carPayloadLoadProgressTitle={t('carPayloadLoadProgressTitle')}
                                onDone={() => setAddPayloadPopupVisible(false)}
                                onCancel={() => setAddPayloadPopupVisible(false)}
                                maxTrailerCapacity={props.maxTrailerCapacity}
                                remainingTrailerSpace={props.remainingTrailerSpace}
                                calculateRemainingTrailerSpace={
                                    props.calculateRemainingTrailerSpace
                                }
                                filledTrailerCapacity={props.filledTrailerCapacity}
                            />
                        </Popup>
                    )}
                    <header className={classNames(styles.header)}>
                        <RouteLocationModelSimpleText
                            inspectorId={OrderForwardStepFormHeaderId}
                            data={props.data?.location}
                        />

                        <div>
                            {!props.isReadOnly && (
                                <Button
                                    inspectorId={orderStepsForwardRemoveStepButtonId}
                                    //
                                    onClick={removeStepStart}
                                />
                            )}
                        </div>
                    </header>
                    <div className={classNames(styles.forwardStepDetailsForm)}>
                        <Form
                            inspectorId={OrderForwardStepFormFormId}
                            className={classNames(styles.orderForwardStepForm)}
                            formData={formData}
                            readOnly={props.isReadOnly}
                            onFieldDataChanged={onFieldDataChanged}
                        />
                        {!props.isReadOnly && (
                            <CheckBox
                                className={classNames(styles.showExternalLocationsCheckBox)}
                                inspectorId={orderStepsForwardShowExternalLocationsCheckBoxId}
                                value={showExternalLocations}
                                onValueChange={setShowExternalLocations}
                            />
                        )}
                    </div>
                    <div className={classNames(styles.productsDatagrid)}>
                        <div className={classNames(styles.headerWrapper)}>
                            <label>{t('products')}</label>
                            {!props.isReadOnly && (
                                <Button
                                    stylingMode="text"
                                    inspectorId={orderStepsForwardAddPayloadButtonId}
                                />
                            )}
                        </div>
                        <DataGrid
                            inspectorId={OrderForwardStepFormGridId}
                            dataSource={payloadDataSource}
                            disableInspectorForm
                            editing={{
                                allowDeleting: !props.isReadOnly,
                                useIcons: false,
                            }}
                        >
                            <Template
                                name="PayloadCarrierCountSimpleText"
                                render={PayloadCarrierCountSimpleTextTemplate}
                            />
                            <Template
                                name="AllowedStacksHeader"
                                render={AllowedStacksHeaderTemplate}
                            />
                        </DataGrid>
                    </div>
                </form>
            </InspectorVariablesContext>
        </div>
    );
});
export default OrderForwardStepForm;

export type OrderForwardStepFormProps = {
    data?: OrderForwardStepFormData;
} & OrderForwardStepFormDisplayOptions;

export type OrderForwardStepFormData = OrderForwardStepData;
export const OrderForwardStepFormDataKeys = OrderForwardStepDataKeys;

export type OrderForwardStepPayloadItemData = PayloadAddFormPayloadData;
export const OrderForwardStepPayloadItemDataKeys = PayloadAddFormPayloadDataKeys;

export interface OrderForwardStepFormDisplayOptions {
    className?: string;
    //
    createPayloadDataSource?: (
        payloadCollectionId?: string
    ) => DataSource | DataSourceOptions | undefined;
    createDestinationLocationsDataSource?: (
        isInternal?: boolean
    ) => DataSource | DataSourceOptions | undefined;
    //
    onFieldDataChanged?: (dataField?: string, value?: any) => void;
    //
    setPlannedStartDate?: (payload: setPlannedStartDatePayload) => Promise<void>;
    setLocation?: (payload: setLocationPayload) => Promise<void>;
    //
    removeStep?: (payload: deleteItemPayload) => Promise<void>;
    onStepsForwardDataChanged?: () => void;
    calculateRemainingTrailerSpace?: () => Promise<void>;
    //
    isReadOnly?: boolean;
    maxTrailerCapacity?: number;
    remainingTrailerSpace?: RemainingTrailerSpace;
    filledTrailerCapacity?: number;
}
